import type { ReadingPosition } from './useBookUrl'
import type { TocItemData } from '~/utils/literature/hierarchy-builder'
import { onWatcherCleanup } from 'vue'

export interface VisiblePositionData {
  position: ReadingPosition
  tocEntry: TocItemData | null
}

interface UseVisiblePositionOptions {
  container: Ref<HTMLElement | null>
  tocEntries: MaybeRefOrGetter<TocItemData[]>
}

/** Track which page/section is visible by finding element closest to viewport center. */
export function useVisiblePosition(options: UseVisiblePositionOptions) {
  const { container, tocEntries } = options

  const observedElements = new Set<HTMLElement>()
  const visiblePosition = ref<ReadingPosition>({ pageNumber: undefined, anchor: undefined })
  const activeTocEntry = ref<TocItemData | null>(null)
  let lastPageNumber: string | undefined
  let lastAnchor: string | undefined

  function findClosestToCenter(containerEl: HTMLElement): { element: HTMLElement, pageNumber: string, anchor: string | undefined } | null {
    const containerRect = containerEl.getBoundingClientRect()
    const centerY = containerRect.top + containerRect.height / 2

    let closestElement: HTMLElement | null = null
    let closestDistance = Infinity

    for (const element of observedElements) {
      const rect = element.getBoundingClientRect()

      if (rect.bottom < containerRect.top || rect.top > containerRect.bottom) {
        continue
      }

      const elementCenterY = rect.top + rect.height / 2
      const distance = Math.abs(elementCenterY - centerY)

      if (distance < closestDistance) {
        closestDistance = distance
        closestElement = element
      }
    }

    if (!closestElement) {
      return null
    }

    const pageNumber = closestElement.getAttribute('data-citation-page')
    if (!pageNumber) {
      return null
    }

    const anchor = closestElement.id || undefined

    return { element: closestElement, pageNumber, anchor }
  }

  function findTocEntry(pageNumber: string, anchor: string | undefined): TocItemData | null {
    const entries = toValue(tocEntries)
    return entries.find(it =>
      (anchor && it.fragmentId === anchor)
      || pageNumber === it.pageNumberString,
    ) ?? null
  }

  function handleScroll() {
    const containerEl = container.value
    if (!containerEl) {
      return
    }

    const closest = findClosestToCenter(containerEl)
    if (!closest) {
      return
    }

    const { pageNumber, anchor } = closest

    if (pageNumber !== lastPageNumber || anchor !== lastAnchor) {
      lastPageNumber = pageNumber
      lastAnchor = anchor

      visiblePosition.value = { pageNumber, anchor }
      activeTocEntry.value = findTocEntry(pageNumber, anchor)
    }
  }

  watch(container, (containerEl) => {
    if (containerEl) {
      containerEl.addEventListener('scroll', handleScroll, { passive: true })
      handleScroll()
    }

    onWatcherCleanup(() => {
      containerEl?.removeEventListener('scroll', handleScroll)
    })
  }, {
    immediate: true,
  })

  return {
    visiblePosition: readonly(visiblePosition),
    activeTocEntry: activeTocEntry as Readonly<Ref<TocItemData | null>>,
    observe(element: HTMLElement) {
      observedElements.add(element)
    },
    unobserve(element: HTMLElement | null) {
      if (element) {
        observedElements.delete(element)
      }
    },
  }
}
